// Reading another system's invoice CSV through a mapping (#41): which column holds
// which part of an invoice. The database checks the lines (db/048); this only turns
// the file's rows into plain lines, the way the mapping says.
import { parseCsv } from '../settings/importFile';
import type { InvoiceImportLine, ImportMappingColumns } from '../../data/source';

export type Field = keyof ImportMappingColumns;
export type DateOrder = 'dmy' | 'mdy' | 'ymd';

/** The invoice's fields, in the order the screen offers them, with the words that usually head their column. */
export const FIELDS: { key: Field; label: string; hint?: string; required?: boolean; guess: string[] }[] = [
  { key: 'number', label: 'Invoice number', required: true, guess: ['invoice number', 'invoice no', 'inv no', 'number', 'reference', 'ref', 'doc no', 'document number'] },
  { key: 'type', label: 'Type', hint: 'Invoice or credit note; without it, a negative amount is a credit note', guess: ['type', 'invoice type', 'transaction type', 'doc type'] },
  { key: 'date', label: 'Date', required: true, guess: ['invoice date', 'date', 'inv date', 'tax point', 'issue date'] },
  { key: 'due_date', label: 'Due date', guess: ['due date', 'due'] },
  { key: 'customer_code', label: 'Account code', hint: 'The customer’s code, as in the Ledger', required: true, guess: ['account code', 'account', 'a/c', 'a/c ref', 'customer code', 'customer ref', 'customer account', 'acc code'] },
  { key: 'customer_name', label: 'Account name', guess: ['account name', 'customer name', 'customer', 'name'] },
  { key: 'order_no', label: 'Order number', hint: 'Shown as their reference', guess: ['order number', 'order no', 'po number', 'po', 'purchase order', 'customer order'] },
  { key: 'description', label: 'Description', guess: ['description', 'details', 'narrative', 'item'] },
  { key: 'nominal_code', label: 'Nominal code', required: true, guess: ['nominal code', 'nominal', 'n/c', 'gl code', 'account number', 'sales account'] },
  { key: 'vat_rate', label: 'VAT rate', hint: 'Such as 23 or 13.5', guess: ['vat rate', 'tax rate', 'rate', 'vat %'] },
  { key: 'vat_code', label: 'VAT code', hint: 'Only if it holds the Ledger’s own codes (S23, R135…)', guess: ['vat code', 'tax code'] },
  { key: 'net', label: 'Net amount', guess: ['net amount', 'net', 'nett amount', 'nett', 'net value', 'goods'] },
  { key: 'vat', label: 'VAT amount', guess: ['vat amount', 'vat', 'tax amount', 'tax'] },
  { key: 'gross', label: 'Gross amount', guess: ['gross amount', 'gross', 'total', 'invoice total', 'amount'] },
  { key: 'currency', label: 'Currency', guess: ['currency', 'currency code', 'ccy'] },
];

const norm = (s: string) => s.trim().toLowerCase().replace(/[_*.]+/g, ' ').replace(/\s+/g, ' ');

/** A first guess at the mapping from the file's headings: the closest heading for each field, each heading used once. */
export function guessColumns(headings: string[]): ImportMappingColumns {
  const left = new Map(headings.map((h) => [norm(h), h] as const));
  const out: ImportMappingColumns = {};
  for (const f of FIELDS) {
    const hit = f.guess.find((g) => left.has(g));
    if (hit) { out[f.key] = left.get(hit)!; left.delete(hit); }
  }
  return out;
}

/** The headings a saved mapping names that this file doesn't have. */
export function missingHeadings(columns: ImportMappingColumns, headings: string[]): string[] {
  const have = new Set(headings.map(norm));
  return Object.values(columns).filter((h): h is string => !!h && !have.has(norm(h)));
}

/** What the mapping still needs before the file can be checked. */
export function mappingProblem(columns: ImportMappingColumns): string | null {
  const need = FIELDS.filter((f) => f.required && !columns[f.key]).map((f) => f.label);
  if (!columns.net && !columns.gross) need.push('Net amount (or Gross amount)');
  return need.length ? `Choose the column for: ${need.join(', ')}` : null;
}

/** "1,234.50", "€1,234.50", "(12.00)", "12.00-" and "-12" as plain numbers ("" when blank). */
export function readAmount(s: string | undefined): string {
  let t = (s ?? '').trim().replace(/[€£$\s]/g, '').replace(/,/g, '');
  if (!t) return '';
  let neg = false;
  if (/^\(.*\)$/.test(t)) { neg = true; t = t.slice(1, -1); }
  if (t.endsWith('-')) { neg = true; t = t.slice(0, -1); }
  if (t.startsWith('-')) { neg = !neg; t = t.slice(1); }
  if (!/^\d*\.?\d+$/.test(t)) return (s ?? '').trim(); // left for the check to say it isn't a number
  return (neg ? '-' : '') + t;
}

/** "23", "23%", "23.0 %" as "23"; "0.23" stays (the check reads it as 23%). */
export const readRate = (s: string | undefined) => (s ?? '').trim().replace(/\s*%$/, '');

/** A date as yyyy-mm-dd, read in the file's order (05/10/2026 is 5 October for dmy); left as it was if it can't be read. */
export function readDate(s: string | undefined, order: DateOrder): string {
  const t = (s ?? '').trim();
  if (!t) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
  const parts = t.split(/[/.\- ]+/).filter(Boolean);
  if (parts.length !== 3) return t;
  let [d, m, y] = order === 'dmy' ? parts : order === 'mdy' ? [parts[1], parts[0], parts[2]] : [parts[2], parts[1], parts[0]];
  const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const named = months.indexOf((m ?? '').slice(0, 3).toLowerCase());
  if (named >= 0) m = String(named + 1);
  if (!/^\d{1,2}$/.test(d ?? '') || !/^\d{1,2}$/.test(m ?? '') || !/^\d{2}(\d{2})?$/.test(y ?? '')) return t;
  if (y!.length === 2) y = `20${y}`;
  const iso = `${y}-${m!.padStart(2, '0')}-${d!.padStart(2, '0')}`;
  const back = new Date(`${iso}T12:00:00Z`);
  return Number.isNaN(back.getTime()) || back.toISOString().slice(0, 10) !== iso ? t : iso;
}

export type ReadFile = { headings: string[]; rows: string[][]; hasHeadings: boolean };

/** Default words for the Type column (#48); each mapping keeps its own. */
export const DEFAULT_INVOICE_VALUES = ['Invoice', 'INV', 'SI'];
export const DEFAULT_CREDIT_VALUES = ['Credit', 'Credit note', 'CRN', 'SC'];

/** "SI, Invoice" as ["SI", "Invoice"]. */
export const splitValues = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

/**
 * The file's headings and rows, blank lines kept so row numbers match the spreadsheet's. With hasHeadings
 * false (#47: the first line is already an invoice), the columns are called Column 1, Column 2…
 */
export function readFile(text: string, hasHeadings = true): ReadFile {
  const all = parseCsv(text, true);
  const first = all.findIndex((r) => r.some((c) => c.trim() !== ''));
  if (first < 0) return { headings: [], rows: [], hasHeadings };
  if (!hasHeadings) {
    const width = Math.max(...all.map((r) => r.length));
    return { headings: Array.from({ length: width }, (_, i) => `Column ${i + 1}`), rows: all.slice(first).map((r, i) => ['' + (first + i + 1), ...r]), hasHeadings };
  }
  return { headings: all[first]!.map((h) => h.trim()), rows: all.slice(first + 1).map((r, i) => ['' + (first + i + 2), ...r]), hasHeadings };
}

/** How a column is offered in the list (#44): its heading and number, or (no heading row) its number and first value. */
export function columnLabel(file: ReadFile, i: number): string {
  if (file.hasHeadings) return `${file.headings[i] || '(no heading)'} (column ${i + 1})`;
  const sample = file.rows.find((r) => (r[i + 1] ?? '').trim() !== '')?.[i + 1]?.trim();
  return sample ? `Column ${i + 1} (${sample.length > 24 ? `${sample.slice(0, 24)}…` : sample})` : `Column ${i + 1}`;
}

/** Fields mapped to a column another field also uses: field → the other fields' labels. */
export function doubledColumns(columns: ImportMappingColumns): Partial<Record<Field, string[]>> {
  const out: Partial<Record<Field, string[]>> = {};
  for (const f of FIELDS) {
    const h = columns[f.key];
    if (!h) continue;
    const others = FIELDS.filter((g) => g.key !== f.key && columns[g.key] && norm(columns[g.key]!) === norm(h)).map((g) => g.label);
    if (others.length) out[f.key] = others;
  }
  return out;
}

/** The file's rows as plain lines through the mapping; blank rows and totals rows (no number and no account) left out. */
export function toLines(file: ReadFile, columns: ImportMappingColumns, order: DateOrder): InvoiceImportLine[] {
  const at = (h: string | undefined) => (h ? file.headings.findIndex((x) => norm(x) === norm(h)) : -1);
  const idx = Object.fromEntries(FIELDS.map((f) => [f.key, at(columns[f.key])])) as Record<Field, number>;
  const lines: InvoiceImportLine[] = [];
  for (const [rowNo, ...cells] of file.rows) {
    const get = (k: Field) => (idx[k] >= 0 ? (cells[idx[k]] ?? '').trim() : '');
    if (!get('number') && !get('customer_code')) continue;
    const line: InvoiceImportLine = { row_no: Number(rowNo) };
    const put = (k: Field, v: string) => { if (v !== '') (line as Record<string, unknown>)[k] = v; };
    put('type', get('type'));
    put('customer_code', get('customer_code'));
    put('customer_name', get('customer_name'));
    put('number', get('number'));
    put('date', readDate(get('date'), order));
    put('due_date', readDate(get('due_date'), order));
    put('order_no', get('order_no'));
    put('description', get('description'));
    put('nominal_code', get('nominal_code'));
    put('vat_code', get('vat_code'));
    put('vat_rate', readRate(get('vat_rate')));
    put('net', readAmount(get('net')));
    put('vat', readAmount(get('vat')));
    put('gross', readAmount(get('gross')));
    put('currency', get('currency'));
    lines.push(line);
  }
  return lines;
}
